import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface WizardStep {
  key: string;
  label: string;
  icon?: React.ReactNode;
}

interface WizardStepperProps {
  steps: WizardStep[];
  currentStep: string;
  onStepClick?: (stepKey: string) => void;
}

/**
 * WizardStepper
 *
 * Обобщённый stepper для wizard-форм.
 * Визуальный паттерн аналогичен StepIndicator из triggers.
 *
 * - Шаги до текущего: кликабельные, с иконкой Check
 * - Текущий шаг: активный стиль
 * - Шаги после текущего: disabled
 */
export function WizardStepper({ steps, currentStep, onStepClick }: WizardStepperProps) {
  const currentIndex = steps.findIndex((s) => s.key === currentStep);

  return (
    <nav aria-label="Progress" className="mb-8">
      <ol className="flex items-center justify-center gap-2 md:gap-4">
        {steps.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const canClick = onStepClick !== undefined && index <= currentIndex;

          return (
            <li key={step.key} className="flex items-center">
              <button
                type="button"
                onClick={() => canClick && onStepClick(step.key)}
                disabled={!canClick}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors',
                  isCompleted && 'text-primary hover:bg-primary/5',
                  isCurrent && 'bg-primary/10 text-primary font-medium',
                  !isCompleted && !isCurrent && 'text-muted-foreground',
                  canClick && 'cursor-pointer',
                  !canClick && 'cursor-default',
                )}
              >
                <span
                  className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium',
                    isCompleted && 'bg-primary text-primary-foreground',
                    isCurrent && 'border-2 border-primary text-primary',
                    !isCompleted && !isCurrent && 'border-2 border-muted-foreground/30',
                  )}
                >
                  {isCompleted ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    step.icon ?? index + 1
                  )}
                </span>
                <span className="hidden sm:inline">{step.label}</span>
              </button>

              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'ml-2 md:ml-4 h-0.5 w-8 md:w-12',
                    index < currentIndex ? 'bg-primary' : 'bg-muted-foreground/30',
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
